/** Reference viewport from PraxisPlay HCP carousel design (1440×950). */
export const PRAXIS_PLAY_VIEWPORT = {
  width: 1440,
  height: 950,
  /** Usable HCP content region in the reference layout (after page chrome). */
  contentWidth: 1100,
  contentHeight: 580,
  characterCardWidth: 275,
  characterCardHeight: 480,
  carouselHeight: 480,
  cardGap: 20,
} as const;

/**
 * Cards keep their reference size (275×480) up to roughly a 16" laptop and only
 * grow proportionally beyond that. These thresholds approximate a 16" screen
 * viewport (e.g. a 16" MacBook Pro is ~1728×1117 logical px). At or below the
 * threshold the scale is pinned to 1; above it the card scales with the window.
 */
export const PRAXIS_PLAY_SCALE_START = {
  width: 1600,
  height: 1000,
} as const;

/** Upper bound on scale so cards stay sensible on very large / 4K displays. */
export const PRAXIS_PLAY_MAX_SCALE = 1.85;

/** Minimum visible card height (old responsive-height floor). */
export const PRAXIS_PLAY_MIN_CARD_HEIGHT = 340;

/** Maximum card height at/below the ~16" threshold (must stay below 400px). */
export const PRAXIS_PLAY_MAX_CARD_HEIGHT_SMALL = 395;

/** Reference card-height : viewport-height ratio (480 / 950), the old logic. */
const REFERENCE_HEIGHT_RATIO =
  PRAXIS_PLAY_VIEWPORT.carouselHeight / PRAXIS_PLAY_VIEWPORT.height;

export type PraxisPlayViewportMetrics = {
  characterCardWidth: number;
  characterCardHeight: number;
  carouselHeight: number;
  cardGap: number;
  /** Uniform scale relative to the reference card (1 up to the 16" threshold). */
  scale: number;
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Old responsive card height: proportional to the window height, floored so it
 * never collapses on short windows and capped below 400px. Used at/below the
 * ~16" threshold.
 */
function getOldCardHeight(viewportHeight: number) {
  return clamp(
    REFERENCE_HEIGHT_RATIO * viewportHeight,
    PRAXIS_PLAY_MIN_CARD_HEIGHT,
    PRAXIS_PLAY_MAX_CARD_HEIGHT_SMALL,
  );
}

/**
 * Card size at the exact moment `scale` reaches 1 (the ~16" threshold). The old
 * responsive height is already pinned to its cap here, so anchoring the
 * above-threshold sizing to this size makes crossing the boundary continuous:
 * no jump in size or aspect ratio, and the card box grows by the same uniform
 * `scale` that the card's content is scaled by.
 */
const ANCHOR_CARD_HEIGHT = getOldCardHeight(PRAXIS_PLAY_SCALE_START.height);
const ANCHOR_CARD_WIDTH = PRAXIS_PLAY_VIEWPORT.characterCardWidth;

export function getPraxisPlayViewportMetrics(
  viewportWidth: number,
  viewportHeight: number,
  options?: { isMobile?: boolean },
): PraxisPlayViewportMetrics {
  if (options?.isMobile) {
    return {
      characterCardWidth: 150,
      characterCardHeight: 380,
      carouselHeight: 380,
      cardGap: 12,
      scale: 0.85,
    };
  }

  const width =
    viewportWidth > 0 ? viewportWidth : PRAXIS_PLAY_SCALE_START.width;
  const height =
    viewportHeight > 0 ? viewportHeight : PRAXIS_PLAY_SCALE_START.height;

  const scaleW = width / PRAXIS_PLAY_SCALE_START.width;
  const scaleH = height / PRAXIS_PLAY_SCALE_START.height;

  // Width / content scale: pinned to 1 up to the ~16" threshold; grows
  // proportionally with the window above it (capped for 4K). This drives card
  // width, gap, fonts, image and badge sizes.
  const scale = clamp(Math.min(scaleW, scaleH), 1, PRAXIS_PLAY_MAX_SCALE);
  const aboveThreshold = scale > 1;

  // Above the threshold the card grows uniformly by `scale` from its size at
  // the threshold (ANCHOR_*), so both dimensions scale by the same factor the
  // content is scaled by and the boundary is crossed continuously (no jump in
  // size or aspect ratio). At/below the threshold, use the old responsive
  // height logic so smaller laptops behave exactly as before.
  const characterCardHeight = aboveThreshold
    ? Math.round(ANCHOR_CARD_HEIGHT * scale)
    : Math.round(getOldCardHeight(height));

  const characterCardWidth = aboveThreshold
    ? Math.round(ANCHOR_CARD_WIDTH * scale)
    : PRAXIS_PLAY_VIEWPORT.characterCardWidth;

  return {
    characterCardWidth,
    characterCardHeight,
    carouselHeight: characterCardHeight,
    cardGap: Math.round(PRAXIS_PLAY_VIEWPORT.cardGap * scale),
    scale,
  };
}

/** Legacy image-carousel sizing (same reference viewport). */
export function getPraxisPlayImageCarouselHeight(
  viewportWidth: number,
  viewportHeight: number,
): { height: number; width: number } {
  const height =
    (PRAXIS_PLAY_VIEWPORT.carouselHeight / PRAXIS_PLAY_VIEWPORT.height) *
    viewportHeight;
  const width = (226 / PRAXIS_PLAY_VIEWPORT.width) * viewportWidth;
  return { height, width };
}
